import { useState } from 'react';
import { InlineStack, Select } from '@customerio/pluma-components/react';
import { fieldError } from '@customerio/dryad';
import { useBuilderActions, useLeafState, useMeta, useNodeErrors } from '@customerio/dryad-react';
import { useEnvironmentOptional } from '@/services/db/environment/queries';
import type { ReactNode } from 'react';
import {
  CONSENT_QUALIFIER_FIELDS,
  consentActionOf,
  consentActionOptions,
  consentQualifierFieldLabel,
  consentQualifierFieldOf,
  consentQualifierOptions,
} from './consent-types';
import type { ConsentConditionState } from './consent-types';

type ConsentRowProps = {
  /** The leaf node's id, supplied by the group to every registered view. */
  id: string;
  /** The plugin type gating the typed read — the surface's consent leaf. */
  conditionType: string;
  /** The condition-type select, where the surface offers more than one type. */
  typeSelect?: ReactNode;
};

const NEGATE_OPTIONS = [
  { value: 'false', label: 'has' },
  { value: 'true', label: 'has not' },
];

const ANY_QUALIFIER = '';

const QUALIFIER_FIELD_OPTIONS = [
  { value: ANY_QUALIFIER, label: 'For any reason' },
  ...CONSENT_QUALIFIER_FIELDS.map((field) => ({ value: field, label: consentQualifierFieldLabel(field) })),
];

/** One qualifier only: two would AND across a person's records, not within one. */
export function ConsentRow({ id, conditionType, typeSelect }: ConsentRowProps) {
  const meta = useMeta();
  const { state, node } = useLeafState<ConsentConditionState>(id, conditionType);
  const { updateState } = useBuilderActions();
  const errors = useNodeErrors(id);
  const environment = useEnvironmentOptional();
  const enabledActions = environment.data?.environment?.enabled_actions ?? [];

  // Flag the value only once visited, so a new row doesn't open red.
  const [valueTouched, setValueTouched] = useState(() => state.value != null);
  const valueErrorMessage = valueTouched ? fieldError(errors, 'value') : undefined;

  return (
    <InlineStack data-testid="dryad-condition-row" gap="050" alignBlock="baseline" wrap>
      {typeSelect}
      <Select
        data-testid="dryad-consent-negate-select"
        ariaLabel="Consent match"
        size="sm"
        options={NEGATE_OPTIONS}
        value={state.inverse ? 'true' : 'false'}
        onChange={(value: string | null) => {
          if (value != null && node != null) {
            updateState(node, { inverse: value === 'true' });
          }
        }}
        isDisabled={meta.isDisabled}
      />
      <Select
        data-testid="dryad-consent-action-select"
        ariaLabel="Consent action"
        size="sm"
        options={consentActionOptions(state.action)}
        value={state.action}
        onChange={(value: string | null) => {
          const action = value == null ? null : consentActionOf(value);
          if (action != null && node != null) {
            updateState(node, { action });
          }
        }}
        isDisabled={meta.isDisabled}
      />
      <Select
        data-testid="dryad-consent-qualifier-select"
        ariaLabel="Consent qualifier"
        size="sm"
        options={QUALIFIER_FIELD_OPTIONS}
        value={state.field ?? ANY_QUALIFIER}
        onChange={(value: string | null) => {
          if (value == null || node == null) {
            return;
          }
          // Values are per field, so switching clears the chosen one.
          updateState(node, { field: consentQualifierFieldOf(value), value: null });
          setValueTouched(false);
        }}
        isDisabled={meta.isDisabled}
      />
      {state.field == null ? null : (
        <Select
          data-testid="dryad-consent-value-select"
          ariaLabel={consentQualifierFieldLabel(state.field)}
          size="sm"
          options={consentQualifierOptions(state.field, enabledActions, state.value)}
          value={state.value}
          placeholder={`Select a ${consentQualifierFieldLabel(state.field).toLowerCase()}`}
          error={valueErrorMessage}
          onChange={(value: string | null) => {
            if (value != null && node != null) {
              updateState(node, { value });
            }
          }}
          onOpenChange={(isOpen: boolean) => {
            if (!isOpen) {
              setValueTouched(true);
            }
          }}
          isDisabled={meta.isDisabled}
        />
      )}
    </InlineStack>
  );
}
